/**
 * CredentialManager Component
 *
 * Admin component for managing credentials with:
 * - Tab-based navigation (System/Project/Agent scopes)
 * - CRUD operations via REST API
 * - Token masking for security
 * - Expiry warnings
 *
 * @module components/admin/CredentialManager
 */

import { useState, useCallback, useEffect, useMemo, memo } from 'react';
import {
  Key,
  Plus,
  RefreshCw,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  Shield,
  FolderKanban,
  Bot,
  Clock,
  AlertTriangle
} from 'lucide-react';
import CredentialForm from './CredentialForm.jsx';
import CredentialList from './CredentialList.jsx';

// ============================================
// Constants
// ============================================

const API_BASE = '/api';

const CREDENTIAL_SCOPES = {
  SYSTEM: 'system',
  PROJECT: 'project',
  AGENT: 'agent'
};

const CREDENTIAL_TYPES = {
  REPOSITORY: 'repository',
  API: 'api',
  CUSTOM: 'custom'
};

const SCOPE_TABS = [
  { id: CREDENTIAL_SCOPES.SYSTEM, label: 'System', icon: Shield, description: 'Global credentials for all agents' },
  { id: CREDENTIAL_SCOPES.PROJECT, label: 'Project', icon: FolderKanban, description: 'Project-specific credentials' },
  { id: CREDENTIAL_SCOPES.AGENT, label: 'Agent', icon: Bot, description: 'Agent-private credentials' }
];

// ============================================
// API Functions (placeholder - will connect to real API)
// ============================================

async function fetchCredentials(scope, options = {}) {
  const params = new URLSearchParams();
  params.set('scope', scope);
  if (options.type) params.set('type', options.type);
  if (options.projectId) params.set('projectId', options.projectId);
  if (options.agentId) params.set('agentId', options.agentId);
  if (options.includeExpired) params.set('includeExpired', 'true');

  const response = await fetch(`${API_BASE}/credentials?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch credentials: ${response.statusText}`);
  }
  return response.json();
}

async function createCredential(data) {
  const response = await fetch(`${API_BASE}/credentials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to create credential');
  }
  return response.json();
}

async function updateCredential(id, data) {
  const response = await fetch(`${API_BASE}/credentials/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to update credential');
  }
  return response.json();
}

async function deleteCredential(id) {
  const response = await fetch(`${API_BASE}/credentials/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to delete credential');
  }
  return true;
}

async function fetchProjects() {
  const response = await fetch(`${API_BASE}/projects`);
  if (!response.ok) throw new Error('Failed to fetch projects');
  return response.json();
}

async function fetchAgents() {
  const response = await fetch(`${API_BASE}/agents`);
  if (!response.ok) throw new Error('Failed to fetch agents');
  return response.json();
}

// ============================================
// Utility Functions
// ============================================

/**
 * Check if credential expires within days threshold
 */
function isExpiringSoon(expiresAt, daysThreshold = 7) {
  if (!expiresAt) return false;
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffDays = (expiry - now) / (1000 * 60 * 60 * 24);
  return diffDays > 0 && diffDays <= daysThreshold;
}

/**
 * Check if credential is expired
 */
function isExpired(expiresAt) {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

// ============================================
// Tab Button Component
// ============================================

const TabButton = memo(function TabButton({ tab, isActive, onClick, count = 0 }) {
  const Icon = tab.icon;
  return (
    <button
      onClick={() => onClick(tab.id)}
      className={`
        flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg
        border-b-2 transition-colors
        ${isActive
          ? 'border-indigo-500 text-indigo-400 bg-gray-800'
          : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
        }
      `}
      title={tab.description}
    >
      <Icon className="w-4 h-4" />
      <span>{tab.label}</span>
      {count > 0 && (
        <span className={`
          px-1.5 py-0.5 text-xs rounded-full
          ${isActive ? 'bg-indigo-500/20 text-indigo-300' : 'bg-gray-700 text-gray-400'}
        `}>
          {count}
        </span>
      )}
    </button>
  );
});

// ============================================
// Search & Filter Bar Component
// ============================================

const SearchFilterBar = memo(function SearchFilterBar({
  searchQuery,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  onRefresh,
  isLoading
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      {/* Search */}
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search credentials..."
          className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg
                     text-sm text-gray-200 placeholder-gray-500
                     focus:outline-none focus:border-indigo-500"
        />
      </div>

      {/* Type Filter */}
      <div className="relative">
        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <select
          value={typeFilter}
          onChange={(e) => onTypeFilterChange(e.target.value)}
          className="pl-9 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg
                     text-sm text-gray-200 appearance-none cursor-pointer
                     focus:outline-none focus:border-indigo-500"
        >
          <option value="">All Types</option>
          <option value={CREDENTIAL_TYPES.REPOSITORY}>Repository</option>
          <option value={CREDENTIAL_TYPES.API}>API</option>
          <option value={CREDENTIAL_TYPES.CUSTOM}>Custom</option>
        </select>
      </div>

      {/* Refresh Button */}
      <button
        onClick={onRefresh}
        disabled={isLoading}
        className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800
                   rounded-lg transition-colors disabled:opacity-50"
        title="Refresh"
      >
        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
});

// ============================================
// Scope Selector Component (for Project/Agent tabs)
// ============================================

const ScopeSelector = memo(function ScopeSelector({
  scope,
  items,
  selectedId,
  onSelect,
  isLoading,
  allowAll = false
}) {
  const label = scope === CREDENTIAL_SCOPES.PROJECT ? 'Project' : 'Agent';
  const Icon = scope === CREDENTIAL_SCOPES.PROJECT ? FolderKanban : Bot;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 mb-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading {label.toLowerCase()}s...</span>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="flex items-center gap-2 text-gray-500 mb-4">
        <AlertCircle className="w-4 h-4" />
        <span>No {label.toLowerCase()}s available</span>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <label className="block text-sm text-gray-400 mb-2">
        {allowAll ? `Filter by ${label}` : `Select ${label}`}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <select
          value={selectedId || ''}
          onChange={(e) => onSelect(e.target.value || null)}
          className="w-full max-w-md pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg
                     text-sm text-gray-200 appearance-none cursor-pointer
                     focus:outline-none focus:border-indigo-500"
        >
          <option value="">{allowAll ? `All ${label}s` : `Select a ${label.toLowerCase()}...`}</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.displayName || item.name || item.id}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
});

// ============================================
// Main CredentialManager Component
// ============================================

export function CredentialManager() {
  // State
  const [activeScope, setActiveScope] = useState(CREDENTIAL_SCOPES.SYSTEM);
  const [credentials, setCredentials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Scope-specific selection
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [isScopeDataLoading, setIsScopeDataLoading] = useState(false);

  // Modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);

  // Count credentials per scope (for tab badges)
  const scopeCounts = useMemo(() => ({
    [CREDENTIAL_SCOPES.SYSTEM]: credentials.filter(c => c.isSystem).length,
    [CREDENTIAL_SCOPES.PROJECT]: credentials.filter(c => c.projectId).length,
    [CREDENTIAL_SCOPES.AGENT]: credentials.filter(c => c.agentId).length
  }), [credentials]);

  // Load projects and agents for scope selectors
  useEffect(() => {
    async function loadScopeData() {
      setIsScopeDataLoading(true);
      try {
        const [projectsData, agentsData] = await Promise.all([
          fetchProjects(),
          fetchAgents()
        ]);
        setProjects(Array.isArray(projectsData) ? projectsData : projectsData.data || []);
        setAgents(Array.isArray(agentsData) ? agentsData : agentsData.data || []);
      } catch (err) {
        console.error('Failed to load scope data:', err);
      } finally {
        setIsScopeDataLoading(false);
      }
    }
    loadScopeData();
  }, []);

  // Load credentials based on active scope
  const loadCredentials = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const options = { type: typeFilter || undefined };

      if (activeScope === CREDENTIAL_SCOPES.PROJECT && selectedProjectId) {
        options.projectId = selectedProjectId;
      } else if (activeScope === CREDENTIAL_SCOPES.AGENT && selectedAgentId) {
        options.agentId = selectedAgentId;
      }

      const data = await fetchCredentials(activeScope, options);
      setCredentials(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError(err.message);
      setCredentials([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeScope, typeFilter, selectedProjectId, selectedAgentId]);

  // Reload when scope or selection changes
  useEffect(() => {
    // For system scope, load immediately
    // For project scope, only load if selection is made
    // For agent scope, load always (all agents if none selected, filtered if selected)
    if (activeScope === CREDENTIAL_SCOPES.SYSTEM) {
      loadCredentials();
    } else if (activeScope === CREDENTIAL_SCOPES.PROJECT && selectedProjectId) {
      loadCredentials();
    } else if (activeScope === CREDENTIAL_SCOPES.AGENT) {
      loadCredentials();
    }
  }, [activeScope, selectedProjectId, selectedAgentId, loadCredentials]);

  // Filter credentials by search query
  const filteredCredentials = useMemo(() => {
    if (!searchQuery.trim()) return credentials;
    const query = searchQuery.toLowerCase();
    return credentials.filter(cred =>
      cred.name?.toLowerCase().includes(query) ||
      cred.description?.toLowerCase().includes(query) ||
      cred.type?.toLowerCase().includes(query)
    );
  }, [credentials, searchQuery]);

  // Handlers
  const handleTabChange = useCallback((scopeId) => {
    setActiveScope(scopeId);
    setSearchQuery('');
    setError(null);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingCredential(null);
    setIsFormOpen(true);
  }, []);

  const handleEdit = useCallback((credential) => {
    setEditingCredential(credential);
    setIsFormOpen(true);
  }, []);

  const handleDelete = useCallback(async (credential) => {
    if (!confirm(`Delete credential "${credential.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteCredential(credential.id);
      loadCredentials();
    } catch (err) {
      setError(err.message);
    }
  }, [loadCredentials]);

  const handleFormSubmit = useCallback(async (formData) => {
    try {
      // Add scope-specific data
      const data = { ...formData };
      if (activeScope === CREDENTIAL_SCOPES.SYSTEM) {
        data.isSystem = true;
      } else if (activeScope === CREDENTIAL_SCOPES.PROJECT) {
        data.projectId = selectedProjectId;
      } else if (activeScope === CREDENTIAL_SCOPES.AGENT) {
        data.agentId = selectedAgentId;
      }

      if (editingCredential) {
        await updateCredential(editingCredential.id, data);
      } else {
        await createCredential(data);
      }
      setIsFormOpen(false);
      loadCredentials();
    } catch (err) {
      throw err; // Let form handle the error display
    }
  }, [activeScope, selectedProjectId, selectedAgentId, editingCredential, loadCredentials]);

  const handleFormClose = useCallback(() => {
    setIsFormOpen(false);
    setEditingCredential(null);
  }, []);

  // Check if we can show credentials (scope selection made if needed)
  // Agent scope always shows credentials (all agents if none selected)
  const canShowCredentials = activeScope === CREDENTIAL_SCOPES.SYSTEM ||
    (activeScope === CREDENTIAL_SCOPES.PROJECT && selectedProjectId) ||
    activeScope === CREDENTIAL_SCOPES.AGENT;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Key className="w-6 h-6 text-indigo-400" />
          <h1 className="text-xl font-semibold text-gray-100">Credentials</h1>
        </div>
        <button
          onClick={handleCreate}
          disabled={!canShowCredentials}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500
                     text-white text-sm font-medium rounded-lg transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          <span>Add Credential</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-700 mb-4">
        {SCOPE_TABS.map((tab) => (
          <TabButton
            key={tab.id}
            tab={tab}
            isActive={activeScope === tab.id}
            onClick={handleTabChange}
            count={scopeCounts[tab.id]}
          />
        ))}
      </div>

      {/* Scope Selector (for Project/Agent tabs) */}
      {activeScope === CREDENTIAL_SCOPES.PROJECT && (
        <ScopeSelector
          scope={CREDENTIAL_SCOPES.PROJECT}
          items={projects}
          selectedId={selectedProjectId}
          onSelect={setSelectedProjectId}
          isLoading={isScopeDataLoading}
        />
      )}
      {activeScope === CREDENTIAL_SCOPES.AGENT && (
        <ScopeSelector
          scope={CREDENTIAL_SCOPES.AGENT}
          items={agents}
          selectedId={selectedAgentId}
          onSelect={setSelectedAgentId}
          isLoading={isScopeDataLoading}
          allowAll={true}
        />
      )}

      {/* Search & Filter */}
      {canShowCredentials && (
        <SearchFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          onRefresh={loadCredentials}
          isLoading={isLoading}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/30
                        rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Content */}
      {canShowCredentials ? (
        <CredentialList
          credentials={filteredCredentials}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Shield className="w-12 h-12 mb-4 opacity-50" />
          <p className="text-lg">
            {activeScope === CREDENTIAL_SCOPES.PROJECT
              ? 'Select a project to view its credentials'
              : 'Select an agent to view its credentials'
            }
          </p>
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <CredentialForm
          credential={editingCredential}
          scope={activeScope}
          projectId={selectedProjectId}
          agentId={selectedAgentId}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

export default CredentialManager;
